/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/next";
import type { RadioStation } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  colors,
  constantSemanticColors,
  cssMediaQueryMinWidthTablet,
  getRGBAFromColor,
  numericPercentages,
  numericPixels,
  PlayIcon,
  radius,
  spacing,
  styleDynamics,
  SubscriptionIcon,
  Text,
  useIsDesktopViewport,
} from "@packages/ui";
import Image from "next/image";

const containerStyles: Interpolation = {
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  scrollSnapAlign: "center",
  transition:
    "outline 150ms ease-in, opacity 150ms ease-in, scale 150ms ease-in",
  width: numericPercentages[100],
  outline: `${numericPixels[2]} solid transparent`,
  [cssMediaQueryMinWidthTablet]: {
    outline: `${numericPixels[4]} solid transparent`,
  },
};

const imageContainerStyles: Interpolation = {
  aspectRatio: "4 / 3",
  position: "relative",
  width: numericPercentages[100],
  height: "35vh",
  marginTop: spacing.none,
  [cssMediaQueryMinWidthTablet]: {
    height: "unset",
    marginTop: spacing.xl,
  },
};

const imageStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  background: theme.semanticColors.neutralsMedium,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
  borderRadius: radius.none,
  [cssMediaQueryMinWidthTablet]: {
    borderRadius: radius.s,
  },
});

const gradientBackgroundImageStyles: Interpolation = {
  alignItems: "center",
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  marginBottom: "-1px",
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  paddingTop: spacing.xxxl,
  position: "absolute",
  width: numericPercentages[100],
  zIndex: 1,
};

const textTitleStyles: Interpolation = {
  color: constantSemanticColors.onColorHighest,
  overflow: "hidden",
  textAlign: "center",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: numericPercentages[100],
};

const buttonContainerStyles: Interpolation = {
  paddingBottom: spacing.l,
  paddingTop: spacing.m,
  background: colors.black,
  paddingLeft: spacing.l,
  paddingRight: spacing.l,
  [cssMediaQueryMinWidthTablet]: {
    background: colors.transparent,
    paddingLeft: spacing.none,
    paddingRight: spacing.none,
  },
};

type RadioStationImageHeaderProps = {
  radioStation: RadioStation;
  playCTA: () => void;
};

export const RadioStationImageHeader = ({
  radioStation,
  playCTA,
}: RadioStationImageHeaderProps) => {
  const isDesktop = useIsDesktopViewport();

  const t = useTranslations();

  if (!radioStation) return null;

  const {
    images: { color_hex, large: imageLarge },
    has_access,
    name,
  } = radioStation;

  const rgbaColor = getRGBAFromColor({
    color: color_hex ?? "#000000",
    format: "object",
  });

  const image = (
    <Image src={imageLarge} alt={name} fill={true} css={imageStyles} />
  );

  const playButton = (
    <Button
      isFullWidth={true}
      onClickCapture={() => playCTA()}
      size="l"
      startIcon={has_access ? <PlayIcon.Small /> : <SubscriptionIcon.Filled />}
      variant={isDesktop ? "primary" : "secondary"}
    >
      {t(has_access ? "played_radio_station" : "button_subscribe_to_listen")}
    </Button>
  );

  if (isDesktop)
    return (
      <>
        <Text size="m" type="headline">
          {name}
        </Text>
        <div css={imageContainerStyles}>{image}</div>
        <div css={buttonContainerStyles}>{playButton}</div>
      </>
    );

  return (
    <div css={containerStyles}>
      <div css={imageContainerStyles}>
        <div
          css={[
            gradientBackgroundImageStyles,
            styleDynamics.backgroundGradient({
              red: rgbaColor.r,
              green: rgbaColor.g,
              blue: rgbaColor.b,
              direction: "to bottom",
            }),
          ]}
        >
          <Text size="s" type="headline" css={textTitleStyles}>
            {name}
          </Text>
        </div>
        {image}
      </div>
      <div
        css={[
          buttonContainerStyles,
          styleDynamics.backgroundColorFromRGBAObject(rgbaColor),
        ]}
      >
        {playButton}
      </div>
    </div>
  );
};
